import React, { useEffect, useState } from 'react';
import { Download, MoreVertical, Share, Smartphone, X } from 'lucide-react';

const isStandalone = () => (
  window.matchMedia?.('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

const getInstallPlatform = () => {
  const ua = window.navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  if (isIos) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const InstallAppButton = ({ compact = false }) => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone());
  const platform = getInstallPlatform();

  useEffect(() => {
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (installed) return null;

  const handleInstall = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === 'accepted') setInstallPrompt(null);
      return;
    }
    setShowInstallHelp(true);
  };

  const instructions = platform === 'ios'
    ? {
        title: 'Save it on iPhone or iPad',
        lead: 'Open the Hub in Safari, then:',
        steps: [
          <>Tap the <strong>Share</strong> button.</>,
          <>Choose <strong>Add to Home Screen</strong>.</>,
          <>Tap <strong>Add</strong> to place the TTIC icon on your Home Screen.</>
        ],
        icon: Share
      }
    : platform === 'android'
      ? {
          title: 'Save it on Android',
          lead: 'In Chrome, use the browser menu:',
          steps: [
            <>Tap the <strong>three-dot menu</strong>.</>,
            <>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</>,
            <>Confirm to add the TTIC icon to your Home Screen.</>
          ],
          icon: MoreVertical
        }
      : {
          title: 'Install it on this computer',
          lead: 'In Chrome or Edge:',
          steps: [
            <>Select the <strong>install icon</strong> in the address bar, if shown.</>,
            <>Or open the browser menu and choose <strong>Install TTIC Marketing Hub</strong>.</>,
            <>Confirm to open the Hub in its own app-style window.</>
          ],
          icon: Download
        };
  const InstructionIcon = instructions.icon;

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        className={compact
          ? 'inline-flex min-h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-primary-600 shadow-sm hover:border-primary-200 hover:bg-primary-50'
          : 'btn-outline inline-flex items-center justify-center gap-2'}
        aria-label="Save TTIC Marketing Hub as an app"
      >
        <Download size={18} />
        <span className={compact ? 'hidden sm:inline' : ''}>Save as App</span>
      </button>

      {showInstallHelp && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-gray-950/60 p-3 sm:items-center">
          <section className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="install-app-title">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[22%] bg-white p-1 shadow-lg ring-1 ring-gray-200">
                  <img src="/icon-192.png" alt="TTIC Marketing Hub app icon" className="h-full w-full object-contain" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">TTIC Marketing Hub</p>
                  <h2 id="install-app-title" className="mt-1 text-lg font-bold text-gray-900">{instructions.title}</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInstallHelp(false)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
                aria-label="Close installation instructions"
              >
                <X size={22} />
              </button>
            </div>
            <div className="mt-5 rounded-xl bg-primary-50 p-4 text-primary-900">
              <div className="flex items-center gap-3">
                <InstructionIcon size={23} className="shrink-0 text-primary-600" />
                <p className="text-sm font-semibold">{instructions.lead}</p>
              </div>
              <ol className="mt-3 space-y-2 pl-8 text-sm leading-5 text-gray-700">
                {instructions.steps.map((step, index) => <li key={index} className="list-decimal pl-1">{step}</li>)}
              </ol>
            </div>
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 p-4">
              <Smartphone size={21} className="mt-0.5 shrink-0 text-secondary-600" />
              <p className="text-sm leading-5 text-gray-600">Once saved, it opens in its own app-style window and keeps the same secure TTIC Marketing Hub account.</p>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
